import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

export interface Maintenance {
  id: string;
  user_id: string;
  bateau_id: string;
  titre: string;
  description?: string;
  categorie: string;
  date_echeance: string;
  recurrence: string;
  prix?: number;
  reference_articles?: string;
  statut: string;
  notifications_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export const useMaintenances = () => {
  const [maintenances, setMaintenances] = useState<Maintenance[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const channelRef = useRef<any>(null);
  const fetchSeqRef = useRef(0);

  const fetchMaintenances = useCallback(async () => {
    const mySeq = ++fetchSeqRef.current;
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      console.log('Fetching maintenances for user:', user.id);
      
      const { data, error } = await supabase
        .from('maintenances')
        .select('*')
        .eq('user_id', user.id)
        .order('date_echeance', { ascending: true });

      if (error) {
        console.error('Erreur lors du chargement des maintenances:', error);
        if (mySeq !== fetchSeqRef.current) return;
        setMaintenances([]);
      } else {
        console.log('Maintenances loaded:', data?.length || 0);
        if (mySeq !== fetchSeqRef.current) return;
        setMaintenances(data as Maintenance[]);
      }
    } catch (err) {
      console.error('Erreur:', err);
      if (mySeq !== fetchSeqRef.current) return;
      setMaintenances([]);
    } finally {
      if (mySeq === fetchSeqRef.current) {
        setLoading(false);
      }
    }
  }, [user]);

  // Configuration des mises à jour en temps réel avec gestion appropriée des canaux
  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    // Nettoyer le canal existant avant d'en créer un nouveau
    if (channelRef.current) {
      console.log('Cleaning up existing maintenance channel');
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    console.log('Setting up real-time subscription for maintenances');
    
    // Créer un nouveau canal avec un nom unique basé sur l'utilisateur et un timestamp plus aléatoire
    const randomSuffix = Math.random().toString(36).substring(2, 15);
    const channelName = `maintenances-${user.id}-${Date.now()}-${randomSuffix}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'maintenances',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Real-time maintenance change:', payload);
          // Utiliser un délai pour éviter les conflits avec les mises à jour locales
          setTimeout(() => {
            fetchMaintenances();
          }, 500);
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      console.log('Cleaning up maintenances real-time subscription');
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [user?.id, fetchMaintenances]);

  const ajouterMaintenance = useCallback(async (maintenance: Omit<Maintenance, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('maintenances')
        .insert({
          ...maintenance,
          user_id: user.id
        })
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de l\'ajout de la maintenance:', error);
        return { error };
      } else {
        console.log('Maintenance ajoutée:', data);
        // Mettre à jour l'état local immédiatement
        setMaintenances(prev => [...prev, data as Maintenance].sort((a, b) => 
          new Date(a.date_echeance).getTime() - new Date(b.date_echeance).getTime()
        ));
        return { data };
      }
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  }, [user]);

  const modifierMaintenance = useCallback(async (id: string, modifications: Partial<Maintenance>) => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('maintenances')
        .update(modifications)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de la modification de la maintenance:', error);
        return { error };
      } else {
        console.log('Maintenance modifiée:', data);
        // Mettre à jour l'état local immédiatement avec les données retournées par le serveur
        setMaintenances(prev => 
          prev.map(m => m.id === id ? (data as Maintenance) : m)
        );
        return { data };
      }
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  }, [user]);

  const supprimerMaintenance = useCallback(async (id: string) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    try {
      // Mettre à jour l'état local immédiatement (optimistic update)
      setMaintenances(prev => prev.filter(m => m.id !== id));

      const { data, error } = await supabase
        .from('maintenances')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id)
        .select();

      if (error) {
        console.error('Erreur lors de la suppression de la maintenance:', error);
        // Recharger en cas d'erreur pour restaurer l'état correct
        await fetchMaintenances();
        return { error };
      } else if (!data || data.length === 0) {
        console.error('Suppression échouée: aucune ligne supprimée pour id:', id);
        // Recharger pour restaurer l'état correct
        await fetchMaintenances();
        return { error: 'Aucune ligne supprimée' };
      } else {
        console.log('Maintenance supprimée avec succès:', id, data);
        return { success: true };
      }
    } catch (err) {
      console.error('Erreur:', err);
      await fetchMaintenances();
      return { error: err };
    }
  }, [user, fetchMaintenances]);

  // Charger les maintenances au démarrage
  useEffect(() => {
    fetchMaintenances();
  }, [fetchMaintenances]);

  return {
    maintenances,
    loading,
    ajouterMaintenance,
    modifierMaintenance,
    supprimerMaintenance,
    refetch: fetchMaintenances
  };
};
